import { useCallback, useEffect, useRef, useState, type SyntheticEvent } from 'react'
import { Button, Card, Chip, Input, Meter, ProgressBar, SearchField } from '@heroui/react'
import { api, ApiError, type SignageMedia } from '../lib/api'
import { filmFacts } from '../lib/filmFacts'
import { hoverPlayer } from '../lib/playlistView'
import { accepts } from '../lib/filePicker'
import { filmLength, LIBRARY_ACCEPT, mediaPreview, megabytes } from '../lib/signageEdit'
import { explain } from '../lib/staffErrors'
import { formatMoment } from '../lib/staffText'
import { uploadEach, type UploadRow } from '../lib/uploadQueue'
import { Notice } from '../admin/settingsKit'
import { Icon } from '../ui/icons'
import { Pager, usePaged } from '../ui/Pager'
import { useStaffText } from '../ui/StaffText'
import { Empty, SignageModal, UploadButton } from './signageKit'

const hover = hoverPlayer()

const hoverOn = (e: SyntheticEvent<HTMLElement>) => {
  const v = e.currentTarget.querySelector('video')
  if (v) hover.enter(v)
}
const hoverOff = (e: SyntheticEvent<HTMLElement>) => {
  const v = e.currentTarget.querySelector('video')
  if (v) hover.leave(v)
}

/** Handlers for whatever holds a Thumb: its film plays muted while the pointer or focus is on it. */
export const filmHover = { onMouseEnter: hoverOn, onMouseLeave: hoverOff, onFocus: hoverOn, onBlur: hoverOff }

/** A picture as itself, a film by its still frame (played on hover) or a mark. */
export function Thumb({
  media,
  className = 'signage-thumb',
}: {
  media: Pick<SignageMedia, 'assetId' | 'kind'> & { posterAssetId?: string }
  className?: string
}) {
  if (media.kind === 'image') {
    return (
      <span className={className}>
        <img src={api.assetURL(media.assetId)} alt="" loading="lazy" />
      </span>
    )
  }
  return (
    <span className={className}>
      {media.posterAssetId ? (
        <video src={api.assetURL(media.assetId)} poster={api.assetURL(media.posterAssetId)} preload="none" muted loop playsInline />
      ) : (
        <Icon name="play" size={16} />
      )}
    </span>
  )
}

/** A library file large: a film plays with its controls, a picture is shown whole. */
function PreviewModal({ media, onClose }: { media: SignageMedia; onClose: () => void }) {
  const shown = mediaPreview(media, api.assetURL)
  return (
    <SignageModal title={media.name} onClose={onClose} size="lg">
      {shown.kind === 'video' ? (
        <video className="signage-preview" src={shown.src} poster={shown.poster} controls autoPlay muted playsInline />
      ) : (
        <img className="signage-preview" src={shown.src} alt="" />
      )}
    </SignageModal>
  )
}

/** Uploads a file to the library, measuring a film in this browser first; duplicate when it was already there. */
export async function uploadToLibrary(
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<{ media: SignageMedia; duplicate?: boolean }> {
  const facts: { durationMs: number; poster?: Blob } = file.type.startsWith('video/') ? await filmFacts(file) : { durationMs: 0 }
  return api.uploadSignageMedia(file, {
    durationMs: facts.durationMs,
    ...(facts.poster ? { poster: facts.poster } : {}),
    ...(onProgress ? { onProgress } : {}),
    ...(signal ? { signal } : {}),
  })
}

/** One file of a batch: its progress while it goes, then what became of it. */
export function UploadLine({ row }: { row: UploadRow }) {
  const { say } = useStaffText()
  const note =
    row.state === 'waiting'
      ? say('signage.upload.waiting')
      : row.state === 'sending'
        ? `${Math.round(row.progress * 100)}%`
        : row.state === 'done'
          ? say('signage.upload.done')
          : row.state === 'duplicate'
            ? say('signage.duplicate', { name: row.note ?? '' })
            : row.note
  return (
    <li className="signage-upload" data-state={row.state}>
      <span className="signage-upload__name">{row.name}</span>
      {(row.state === 'waiting' || row.state === 'sending') && (
        <ProgressBar className="signage-upload__bar" size="sm" aria-label={row.name} value={row.progress} maxValue={1}>
          <ProgressBar.Track>
            <ProgressBar.Fill />
          </ProgressBar.Track>
        </ProgressBar>
      )}
      <span className={row.state === 'refused' ? 'signage-upload__refused' : 'signage-upload__note'}>{note}</span>
    </li>
  )
}

/** The organisation's library: upload, rename, delete. */
export function LibraryTab() {
  const { say, lang } = useStaffText()
  const [media, setMedia] = useState<SignageMedia[]>([])
  const [space, setSpace] = useState({ used: 0, limit: 0 })
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const [noticeTone, setNoticeTone] = useState<'danger' | 'warning'>('danger')
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  const [asking, setAsking] = useState('')
  const [previewing, setPreviewing] = useState<SignageMedia | null>(null)
  const [uploads, setUploads] = useState<UploadRow[]>([])
  const [sending, setSending] = useState(false)
  const [dropping, setDropping] = useState(false)
  const [find, setFind] = useState('')
  const q = find.trim().toLowerCase()
  const found = q ? media.filter((m) => m.name.toLowerCase().includes(q)) : media
  const paged = usePaged(found, q)
  // A batch is called off when the tab closes, so none goes on unseen behind another.
  const batch = useRef<AbortController | null>(null)
  useEffect(() => () => batch.current?.abort(), [])
  // The old design versions a delete would take with it, once the server has named them.
  const [versions, setVersions] = useState<{ id: string; n: number } | null>(null)

  const load = useCallback(async () => {
    const r = await api.signageMedia()
    setMedia(r.media)
    setSpace({ used: r.usedBytes, limit: r.limitBytes })
  }, [])
  useEffect(() => {
    load()
      .catch((err) => {
        setNoticeTone('danger')
        setNotice(explain(err, lang, 'common.unreachable'))
      })
      .finally(() => setLoaded(true))
  }, [load, lang])

  const act = async (fn: () => Promise<unknown>, otherwise: 'common.unreachable' | 'signage.uploadFailed') => {
    setBusy(true)
    setNotice('')
    try {
      const done = await fn()
      await load()
      const sent = done as { media?: SignageMedia; duplicate?: boolean } | undefined
      if (sent?.duplicate && sent.media) {
        setNoticeTone('warning')
        setNotice(say('signage.duplicate', { name: sent.media.name }))
      }
    } catch (err) {
      setNoticeTone('danger')
      setNotice(explain(err, lang, otherwise))
    } finally {
      setBusy(false)
      // A refused delete must not leave the next click armed to delete with no confirm.
      setAsking('')
    }
  }

  // Chosen or dropped together, sent one at a time; each file is refused or taken on its own.
  const uploadAll = async (files: File[]) => {
    if (sending || files.length === 0) return
    setSending(true)
    setNotice('')
    const stop = new AbortController()
    batch.current = stop
    try {
      await uploadEach(files, {
        signal: stop.signal,
        accept: (f) => accepts(LIBRARY_ACCEPT, f),
        send: async (f, progress, signal) => {
          const sent = await uploadToLibrary(f, progress, signal)
          await load().catch(() => undefined)
          return { duplicate: sent.duplicate, name: sent.media.name }
        },
        explain: (err) => explain(err, lang, 'signage.uploadFailed'),
        notAccepted: (name) => say('common.fileNotAccepted', { name }),
        onChange: setUploads,
      })
    } finally {
      batch.current = null
      setSending(false)
    }
  }

  // A file only old design versions name asks once more, with their number.
  const remove = async (id: string) => {
    const n = versions?.id === id ? versions.n : 0
    setNotice('')
    setBusy(true)
    try {
      await api.deleteSignageMedia(id, n)
      setVersions(null)
      setAsking('')
      // Once the library changes, "Uploaded" lines are stale; refusals still explain a missing file.
      setUploads((rows) => rows.filter((r) => r.state !== 'done' && r.state !== 'duplicate'))
      await load()
    } catch (err) {
      if (err instanceof ApiError && err.code === 'IN_OLD_VERSIONS') {
        setVersions({ id, n: Number(err.args?.n ?? 0) })
        return
      }
      setVersions(null)
      setAsking('')
      setNoticeTone('danger')
      setNotice(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const upload = (
    <UploadButton accept={LIBRARY_ACCEPT} label={say('signage.upload')} variant="primary" disabled={busy || sending} onFiles={(files) => void uploadAll(files)} />
  )
  const spaceWords = say('signage.space', { used: megabytes(space.used, lang), limit: megabytes(space.limit, lang) })

  return (
    <Card
      className="signage-panel signage-drop"
      data-dropping={dropping || undefined}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return
        // Prevented even while sending, or the browser opens the file in place of the page.
        e.preventDefault()
        if (!sending) setDropping(true)
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropping(false)
      }}
      onDrop={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return
        e.preventDefault()
        setDropping(false)
        void uploadAll([...e.dataTransfer.files])
      }}
    >
      <div className="signage-toolbar">
        <Meter
          className="signage-space"
          aria-label={spaceWords}
          value={Math.min(space.used, space.limit)}
          maxValue={space.limit > 0 ? space.limit : 1}
          valueLabel={spaceWords}
          size="sm"
        >
          <Meter.Track className="signage-space__bar">
            <Meter.Fill />
          </Meter.Track>
          <Meter.Output className="signage-space__words" />
        </Meter>
        {media.length > 10 && (
          <SearchField className="signage-find" aria-label={say('signage.findFile')} value={find} onChange={setFind} variant="secondary">
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={say('signage.findFile')} />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
        )}
        {media.length > 0 && upload}
      </div>
      {uploads.length > 0 && (
        <ul className="signage-uploads" aria-label={say('signage.upload')}>
          {uploads.map((row, i) => (
            <UploadLine key={`${i}:${row.name}`} row={row} />
          ))}
        </ul>
      )}
      {notice && <Notice tone={noticeTone}>{notice}</Notice>}
      {!loaded ? (
        <p className="signage-loading">{say('common.loading')}</p>
      ) : media.length === 0 ? (
        <Empty icon="upload" title={say('signage.library.empty')} action={upload} />
      ) : found.length === 0 ? (
        <Empty icon="search" title={say('signage.noMatch', { q: find.trim() })} />
      ) : (
        <>
          <ul className="signage-grid">
            {paged.rows.map((m) => (
              <li key={m.id}>
                <Card className="signage-file">
                  <button type="button" className="signage-file__face" aria-label={`${say('signage.preview')}: ${m.name}`} onClick={() => setPreviewing(m)} {...filmHover}>
                    <Thumb media={m} className="signage-face" />
                    {filmLength(m) && <span className="signage-badge">{filmLength(m)}</span>}
                  </button>
                  <Card.Content className="signage-file__body">
                    {renaming?.id === m.id ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault()
                          void act(() => api.renameSignageMedia(m.id, renaming.name), 'common.unreachable').then(() => setRenaming(null))
                        }}
                      >
                        <Input
                          fullWidth
                          variant="secondary"
                          className="signage-file__rename"
                          aria-label={say('signage.name')}
                          value={renaming.name}
                          maxLength={80}
                          autoFocus
                          onChange={(e) => setRenaming({ id: m.id, name: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setRenaming(null)
                          }}
                        />
                      </form>
                    ) : (
                      <Card.Title className="signage-file__name" title={m.name}>
                        {m.name}
                      </Card.Title>
                    )}
                    <span className="signage-chips">
                      <Chip size="sm" variant="soft">
                        {say(m.kind === 'video' ? 'signage.film' : 'signage.picture')}
                      </Chip>
                      <Chip size="sm" variant="soft">
                        {megabytes(m.bytes, lang)} {say('signage.size')}
                      </Chip>
                      <span className="signage-file__date">{formatMoment(m.createdAt, lang, { date: true })}</span>
                    </span>
                    <span className="signage-file__used" title={m.usedBy.join(', ') || undefined}>
                      {m.usedBy.length > 0 ? m.usedBy.join(', ') : say('signage.notUsed')}
                    </span>
                  </Card.Content>
                  <Card.Footer className="signage-file__acts">
                    <Button variant="ghost" size="sm" isDisabled={busy} onPress={() => setRenaming({ id: m.id, name: m.name })}>
                      {say('signage.rename')}
                    </Button>
                    <Button
                      variant={asking === m.id ? 'danger-soft' : 'ghost'}
                      size="sm"
                      isDisabled={busy}
                      onPress={() => (asking === m.id ? void remove(m.id) : setAsking(m.id))}
                    >
                      {asking !== m.id
                        ? say('common.delete')
                        : versions?.id === m.id
                          ? say('designs.media.alsoVersions', { n: versions.n })
                          : say('signage.deleteAsk')}
                    </Button>
                  </Card.Footer>
                </Card>
              </li>
            ))}
          </ul>
          <Pager {...paged.pager} free />
        </>
      )}
      {previewing && <PreviewModal media={previewing} onClose={() => setPreviewing(null)} />}
    </Card>
  )
}
